import { DataTable } from "@/components/common/DataTable";
import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ExternalLink,
  Loader2,
  TrendingDown,
  BarChart3,
  Package,
  RefreshCw,
  ChevronDown,
  ArrowLeft,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

type Platform = "willhaben" | "refurbed" | "welhof";
type SortOption = "price_asc" | "price_desc" | "newest";
type Condition = "Tweedehands" | "Refurbished" | "B-Keus" | "Gebraucht";

const PLATFORM_COLORS: Record<Platform, string> = {
  willhaben: "#F37021",
  refurbed: "#00A67E",
  welhof: "#004B93",
};

const ALL_CONDITIONS: Condition[] = ["Tweedehands", "Refurbished", "B-Keus", "Gebraucht"];

function PlatformBadge({ platform }: { platform: string }) {
  const color = PLATFORM_COLORS[platform as Platform] || "hsl(var(--muted-foreground))";
  return (
    <Badge
      className="text-white border-0 text-xs"
      style={{ backgroundColor: color }}
    >
      {platform}
    </Badge>
  );
}

export default function PriceDashboard() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const [platformFilter, setPlatformFilter] = useState<"all" | Platform>("all");
  const [brandFilter, setBrandFilter] = useState<string>("all");
  const [modelFilter, setModelFilter] = useState<string>("");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 2000]);
  const [selectedConditions, setSelectedConditions] = useState<Condition[]>([]);
  const [sort, setSort] = useState<SortOption>("newest");
  const [selectedListing, setSelectedListing] = useState<any | null>(null);
  const [scraping, setScraping] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<{ ids: string[]; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [highlightOutliers, setHighlightOutliers] = useState(true);

  // Fetch listings
  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["price-listings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings_enriched" as any)
        .select("*")
        .order("scraped_at", { ascending: false });
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  // Fetch price history for selected listing
  const { data: priceHistory = [] } = useQuery({
    queryKey: ["price-history", selectedListing?.url],
    queryFn: async () => {
      if (!selectedListing?.url) return [];
      const { data, error } = await supabase
        .from("price_history" as any)
        .select("*")
        .eq("listing_url", selectedListing.url)
        .order("recorded_at", { ascending: true });
      if (error) throw error;
      return (data as any[]) || [];
    },
    enabled: !!selectedListing?.url,
  });

  // Unique brands for filter
  const uniqueBrands = useMemo(() => {
    const brands = listings
      .map((l: any) => l.brand)
      .filter((b: string | null) => b)
      .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)
      .sort();
    return brands as string[];
  }, [listings]);

  // KPIs
  const kpis = useMemo(() => {
    const total = listings.length;
    const avgByPlatform = (p: string) => {
      const items = listings.filter((l: any) => l.platform === p);
      if (!items.length) return 0;
      return items.reduce((s: number, l: any) => s + Number(l.price), 0) / items.length;
    };
    const today = new Date().toISOString().slice(0, 10);
    const todayListings = listings.filter(
      (l: any) => l.scraped_at?.slice(0, 10) === today
    );
    const cheapest = todayListings.length
      ? todayListings.reduce((min: any, l: any) =>
          Number(l.price) < Number(min.price) ? l : min
        , todayListings[0])
      : null;

    // Average savings vs original price
    const withOriginal = listings.filter((l: any) => l.original_price && Number(l.original_price) > 0);
    const avgSavingsPercent = withOriginal.length > 0
      ? withOriginal.reduce((sum: number, l: any) => {
          const savings = ((Number(l.original_price) - Number(l.price)) / Number(l.original_price)) * 100;
          return sum + savings;
        }, 0) / withOriginal.length
      : 0;

    return {
      total,
      avgWillhaben: avgByPlatform("willhaben"),
      avgRefurbed: avgByPlatform("refurbed"),
      avgWelhof: avgByPlatform("welhof"),
      cheapest,
      avgSavingsPercent,
    };
  }, [listings]);

  // Filtered + sorted
  const filtered = useMemo(() => {
    let result = [...listings];
    if (platformFilter !== "all") {
      result = result.filter((l: any) => l.platform === platformFilter);
    }
    if (brandFilter !== "all") {
      result = result.filter((l: any) => l.brand === brandFilter);
    }
    if (modelFilter.trim()) {
      const q = modelFilter.trim().toLowerCase();
      result = result.filter((l: any) => l.model?.toLowerCase().includes(q) || l.title?.toLowerCase().includes(q));
    }
    result = result.filter(
      (l: any) => Number(l.price) >= priceRange[0] && Number(l.price) <= priceRange[1]
    );
    if (selectedConditions.length > 0) {
      result = result.filter((l: any) =>
        selectedConditions.includes(l.condition_label)
      );
    }
    switch (sort) {
      case "price_asc":
        result.sort((a: any, b: any) => Number(a.price) - Number(b.price));
        break;
      case "price_desc":
        result.sort((a: any, b: any) => Number(b.price) - Number(a.price));
        break;
      case "newest":
        result.sort(
          (a: any, b: any) =>
            new Date(b.scraped_at).getTime() - new Date(a.scraped_at).getTime()
        );
        break;
    }
    return result;
  }, [listings, platformFilter, brandFilter, modelFilter, priceRange, selectedConditions, sort]);

  // Outlier detection: flag listings whose price deviates >50% from the median of the same brand
  const outlierIds = useMemo(() => {
    const byBrand = new Map<string, number[]>();
    filtered.forEach((l: any) => {
      const key = (l.brand || "_unknown").toLowerCase();
      const arr = byBrand.get(key) || [];
      arr.push(Number(l.price));
      byBrand.set(key, arr);
    });
    const medians = new Map<string, number>();
    byBrand.forEach((prices, key) => {
      if (prices.length < 3) return;
      const sorted = [...prices].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      medians.set(key, sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2);
    });
    const ids = new Set<string>();
    filtered.forEach((l: any) => {
      const key = (l.brand || "_unknown").toLowerCase();
      const median = medians.get(key);
      if (!median) return;
      const price = Number(l.price);
      if (price > median * 1.5 || price < median * 0.5) ids.add(l.id);
    });
    return ids;
  }, [filtered]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((l: any) => selectedIds.has(l.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((l: any) => l.id)));
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async (ids: string[]) => {
    setDeleting(true);
    try {
      const { error } = await supabase.from("listings").delete().in("id", ids);
      if (error) throw error;
      toast.success(`✓ Removed ${ids.length} listing${ids.length === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      setSelectedListing(null);
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ["price-listings"] });
    } catch (err: any) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const selectOutliers = () => {
    if (outlierIds.size === 0) {
      toast.info("No outliers detected in current view");
      return;
    }
    setSelectedIds(new Set(outlierIds));
    toast.success(`Selected ${outlierIds.size} outlier${outlierIds.size === 1 ? "" : "s"}`);
  };

  const handleScrape = async () => {
    setScraping(true);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-prices");
      if (error) throw error;
      toast.success(`✓ Scraped ${data?.count ?? 0} new listings`);
      queryClient.invalidateQueries({ queryKey: ["price-listings"] });

      // Enrich original prices in small batches (avoids the 150s timeout).
      let totalEnriched = 0;
      for (let i = 0; i < 10; i++) {
        const { data: enrichData, error: enrichErr } = await supabase.functions.invoke("enrich-prices");
        if (enrichErr) break;
        totalEnriched += enrichData?.updated ?? 0;
        if (!enrichData?.processed || enrichData?.remaining === 0) break;
      }
      if (totalEnriched > 0) {
        toast.success(`✓ Enriched ${totalEnriched} original prices`);
        queryClient.invalidateQueries({ queryKey: ["price-listings"] });
      }
    } catch (err: any) {
      toast.error(err.message || "Scraper failed");
    } finally {
      setScraping(false);
    }
  };

  const toggleCondition = (c: Condition) => {
    setSelectedConditions((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
  };

  const chartData = priceHistory.map((ph: any) => ({
    date: format(new Date(ph.recorded_at), "dd MMM"),
    price: Number(ph.price),
  }));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border px-4 py-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-primary hover:underline mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t("common.backToData")}</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold tracking-tight">{t("prices.refurb.title")}</h1>
              <p className="text-sm text-muted-foreground">
                Refurbished washing machine market prices
              </p>
            </div>
          <Button
            onClick={handleScrape}
            disabled={scraping}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {scraping ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Run Scraper Now
          </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 md:px-6 space-y-6">
        {/* KPI Cards */}
        <div className={`grid gap-4 ${isMobile ? "grid-cols-1" : "grid-cols-6"}`}>
          <KpiCard
            title="Total Listings"
            value={kpis.total.toString()}
            icon={<Package className="w-4 h-4" />}
          />
          <KpiCard
            title="Avg willhaben"
            value={`€${kpis.avgWillhaben.toFixed(0)}`}
            icon={<BarChart3 className="w-4 h-4" />}
            accent="#F37021"
          />
          <KpiCard
            title="Avg refurbed"
            value={`€${kpis.avgRefurbed.toFixed(0)}`}
            icon={<BarChart3 className="w-4 h-4" />}
            accent="#00A67E"
          />
          <KpiCard
            title="Avg welhof"
            value={`€${kpis.avgWelhof.toFixed(0)}`}
            icon={<BarChart3 className="w-4 h-4" />}
            accent="#004B93"
          />
          <KpiCard
            title="Avg Savings"
            value={kpis.avgSavingsPercent > 0 ? `${kpis.avgSavingsPercent.toFixed(0)}%` : "—"}
            icon={<TrendingDown className="w-4 h-4" />}
            accent="#10b981"
          />
          <Card className="bg-card border-border">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <TrendingDown className="w-4 h-4" />
                Cheapest Today
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {kpis.cheapest ? (
                <div>
                  <p className="text-lg font-bold text-emerald-600">
                    €{Number(kpis.cheapest.price).toFixed(0)}
                  </p>
                  <a
                    href={kpis.cheapest.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline truncate block"
                  >
                    {kpis.cheapest.title?.slice(0, 40)}…
                  </a>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No data today</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Filter Bar */}
        <Card className="bg-card border-border">
          <CardContent className="py-4 px-4">
            <div className={`flex gap-4 items-end ${isMobile ? "flex-col items-stretch" : "flex-row flex-wrap"}`}>
              {/* Platform */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Platform</label>
                <div className="flex gap-1">
                  {(["all", "willhaben", "refurbed", "welhof"] as const).map((p) => (
                    <Button
                      key={p}
                      size="sm"
                      variant={platformFilter === p ? "default" : "outline"}
                      onClick={() => setPlatformFilter(p)}
                      className={
                        platformFilter === p
                          ? p === "all"
                            ? "bg-primary text-primary-foreground border-0"
                            : `text-white border-0`
                          : ""
                      }
                      style={
                        platformFilter === p && p !== "all"
                          ? { backgroundColor: PLATFORM_COLORS[p] }
                          : undefined
                      }
                    >
                      {p === "all" ? "All" : p}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Brand */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Brand</label>
                <SearchableSelect
                  className="w-[140px] h-9"
                  value={brandFilter}
                  onChange={(v) => setBrandFilter(v ?? "all")}
                  placeholder="All brands"
                  searchPlaceholder="Search brands…"
                  emptyMessage="No brands found"
                  items={[{ value: "all", label: "All brands" }, ...uniqueBrands.map((b) => ({ value: b, label: b }))]}
                />
              </div>

              {/* Model search */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Model</label>
                <Input
                  placeholder="Search model…"
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  className="w-[140px] h-9"
                />
              </div>

              {/* Price Range */}
              <div className="space-y-1.5 min-w-[200px] flex-1 max-w-xs">
                <label className="text-xs text-muted-foreground">
                  Price: €{priceRange[0]} – €{priceRange[1]}
                </label>
                <Slider
                  min={0}
                  max={2000}
                  step={25}
                  value={priceRange}
                  onValueChange={(v) => setPriceRange(v as [number, number])}
                />
              </div>

              {/* Condition multi-select */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Condition</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      {selectedConditions.length
                        ? `${selectedConditions.length} selected`
                        : "All conditions"}
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2">
                    {ALL_CONDITIONS.map((c) => (
                      <label
                        key={c}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={selectedConditions.includes(c)}
                          onCheckedChange={() => toggleCondition(c)}
                        />
                        {c}
                      </label>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Sort */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Sort</label>
                <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
                  <SelectTrigger className="w-[150px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest first</SelectItem>
                    <SelectItem value="price_asc">Price ↑</SelectItem>
                    <SelectItem value="price_desc">Price ↓</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{filtered.length} listing{filtered.length === 1 ? "" : "s"}</span>
            {outlierIds.size > 0 && (
              <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1">
                <AlertTriangle className="w-3 h-3" />
                {outlierIds.size} potential outlier{outlierIds.size === 1 ? "" : "s"}
              </Badge>
            )}
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={highlightOutliers}
                onCheckedChange={(v) => setHighlightOutliers(!!v)}
              />
              Highlight outliers
            </label>
          </div>
          <div className="flex items-center gap-2">
            {outlierIds.size > 0 && (
              <Button variant="outline" size="sm" onClick={selectOutliers}>
                <AlertTriangle className="w-4 h-4 mr-1.5" />
                Select outliers
              </Button>
            )}
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() =>
                  setConfirmDelete({
                    ids: Array.from(selectedIds),
                    label: `${selectedIds.size} selected listing${selectedIds.size === 1 ? "" : "s"}`,
                  })
                }
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Delete {selectedIds.size}
              </Button>
            )}
          </div>
        </div>

        {/* Listings Table */}
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No listings found. Run the scraper to get started.</p>
              </div>
            ) : (
              <DataTable<any>
                storageKey="price-dashboard-column-widths"
                rows={filtered}
                rowKey={(l) => l.id}
                onRowClick={(l) => setSelectedListing(l)}
                columns={[
                  { key: "select", width: 48,
                    header: <span onClick={(e) => e.stopPropagation()}><Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" /></span>,
                    cell: (l) => <span onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedIds.has(l.id)} onCheckedChange={() => toggleSelectOne(l.id)} aria-label="Select row" /></span> },
                  { key: "platform", header: "Platform", width: 130, sortValue: (l) => l.platform,
                    cell: (l) => <div className="flex items-center gap-1.5"><PlatformBadge platform={l.platform} />{highlightOutliers && outlierIds.has(l.id) && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}</div> },
                  { key: "title", header: "Title", width: 340, sortValue: (l) => l.title?.toLowerCase(),
                    cell: (l) => <span className="block truncate font-medium" title={l.title}>{l.title}</span> },
                  { key: "condition", header: "Condition", width: 130, sortValue: (l) => l.condition_label || l.condition,
                    cell: (l) => <Badge variant="outline" className="text-xs whitespace-nowrap">{l.condition_label || l.condition || "—"}</Badge> },
                  { key: "scraped_at", header: "Last Seen", width: 120, sortValue: (l) => l.scraped_at,
                    cell: (l) => <span className="text-muted-foreground text-sm">{l.scraped_at ? format(new Date(l.scraped_at), "dd MMM HH:mm") : "—"}</span> },
                  { key: "price", header: "Price", width: 90, align: "right", sortValue: (l) => Number(l.price),
                    cell: (l) => <span className="font-semibold text-emerald-600">€{Number(l.price).toFixed(0)}</span> },
                  { key: "original", header: "Original", width: 100, align: "right", sortValue: (l) => Number(l.original_price) || null,
                    cell: (l) => <span className="text-muted-foreground text-sm">{Number(l.original_price) > 0 ? `€${Number(l.original_price).toFixed(0)}` : "—"}</span> },
                  { key: "savings", header: "Savings", width: 100, align: "right",
                    sortValue: (l) => Number(l.original_price) > 0 ? (Number(l.original_price) - Number(l.price)) / Number(l.original_price) : null,
                    cell: (l) => Number(l.original_price) > 0 ? <Badge variant="outline" className="text-emerald-600 border-emerald-200">-{(((Number(l.original_price) - Number(l.price)) / Number(l.original_price)) * 100).toFixed(0)}%</Badge> : "—" },
                ]}
                actionsWidth={88}
                actions={(l) => (
                  <>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="p-2 text-muted-foreground hover:text-primary" aria-label="Open listing"><ExternalLink className="w-4 h-4" /></a>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label="Delete listing"
                      onClick={() => setConfirmDelete({ ids: [l.id], label: l.title?.slice(0, 60) || "this listing" })}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selectedListing} onOpenChange={() => setSelectedListing(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>Listing Details</SheetTitle>
          </SheetHeader>
          {selectedListing && (
            <div className="mt-6 space-y-6">
              {/* Image */}
              {selectedListing.image_url && (
                <div className="rounded-lg overflow-hidden bg-muted flex items-center justify-center h-48">
                  <img
                    src={selectedListing.image_url}
                    alt={selectedListing.title}
                    className="max-h-full object-contain"
                  />
                </div>
              )}

              {/* Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg leading-tight">
                  {selectedListing.title}
                </h3>
                <div className="flex items-center gap-2">
                  <PlatformBadge platform={selectedListing.platform} />
                  <Badge variant="outline">
                    {selectedListing.condition_label || selectedListing.condition || "Unknown"}
                  </Badge>
                </div>
                <p className="text-3xl font-bold text-emerald-600">
                  €{Number(selectedListing.price).toFixed(2)}
                </p>
                {selectedListing.original_price && Number(selectedListing.original_price) > 0 && (
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground line-through">
                      €{Number(selectedListing.original_price).toFixed(2)}
                    </p>
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                      -{((Number(selectedListing.original_price) - Number(selectedListing.price)) / Number(selectedListing.original_price) * 100).toFixed(0)}% vs new
                    </Badge>
                  </div>
                )}
              </div>

              {/* Price History Chart */}
              {chartData.length > 1 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Price History
                  </h4>
                  <div className="h-48 bg-muted rounded-lg p-3">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="hsl(var(--border))"
                        />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                          stroke="hsl(var(--border))"
                        />
                        <YAxis
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                          stroke="hsl(var(--border))"
                          tickFormatter={(v) => `€${v}`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: 8,
                            color: "hsl(var(--foreground))",
                          }}
                          formatter={(value: number) => [`€${value.toFixed(2)}`, "Price"]}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          stroke="#00A67E"
                          strokeWidth={2}
                          dot={{ fill: "#00A67E", r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {chartData.length <= 1 && (
                <p className="text-sm text-muted-foreground italic">
                  Not enough price history data yet for a chart.
                </p>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button asChild className="flex-1">
                  <a
                    href={selectedListing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Listing
                  </a>
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    setConfirmDelete({
                      ids: [selectedListing.id],
                      label: selectedListing.title?.slice(0, 60) || "this listing",
                    })
                  }
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Confirm delete dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove listing{confirmDelete && confirmDelete.ids.length > 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-medium">{confirmDelete?.label}</span> from the price tracker. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (confirmDelete) handleDelete(confirmDelete.ids);
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KpiCard({
  title,
  value,
  icon,
  accent,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  accent?: string;
}) {
  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle
          className="text-xs font-medium flex items-center gap-1.5"
          style={{ color: accent || "hsl(var(--muted-foreground))" }}
        >
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <p className="text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}